import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1759932713888-0f131d54f88e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJsdWUlMjBnbGFzc3xlbnwwfHx8fDE3Nzc4NDM0NzV8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/30"></div>
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <span className="font-display text-xl font-bold tracking-tight">WindowIQX</span>
          </Link>
          <p className="mt-6 font-display text-3xl lg:text-4xl tracking-tight font-bold leading-tight">Welcome back. Your round is waiting.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1 className="font-display text-3xl font-bold">Log in</h1>
          <p className="text-muted-foreground mt-2 text-sm">New to WindowIQX? <Link to="/signup" className="text-primary hover:underline" data-testid="link-to-signup">Create a business</Link></p>
          <div className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="link-forgot-password">Forgot password?</Link>
            </div>
            <button
              type="submit" disabled={busy}
              className="w-full rounded-md bg-primary text-primary-foreground py-2.5 font-medium hover:bg-primary/90 active:scale-[0.98] transition disabled:opacity-50"
              data-testid="login-submit"
            >
              {busy ? "Signing in…" : "Log in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
